import { type RefObject, useEffect, useRef } from 'react';
import type { Size } from '../core/model/types';
import type { Guide } from '../core/selection';
import type { Viewport } from '../core/view';
import { useEditorStore } from '../state/editorStore';
import { useGuideDrag } from './hooks/useGuideDrag';

/**
 * Rulers along the top and left of the stage, drawn on a canvas.
 *
 * Tick spacing is chosen so labels stay ~60 px apart at any zoom, and dragging
 * down from a ruler drops a guide — the interaction every raster editor has
 * trained people to expect.
 */

const RULER = 20;
const STEPS = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000];

function niceStep(zoom: number): number {
  for (const step of STEPS) if (step * zoom >= 60) return step;
  return 10_000;
}

function themeColors(canvas: HTMLCanvasElement) {
  const style = getComputedStyle(canvas);
  const read = (name: string, fallback: string) => style.getPropertyValue(name).trim() || fallback;
  return {
    bg: read('--color-surface', '#1b1e24'),
    line: read('--color-line', '#333944'),
    ink: read('--color-muted', '#9aa4b2'),
    accent: read('--color-accent', '#4c8dff'),
  };
}

interface DrawArgs {
  axis: 'x' | 'y';
  length: number;
  viewport: Viewport;
  doc: Size;
  guides: readonly Guide[];
}

function draw(canvas: HTMLCanvasElement, args: DrawArgs): void {
  const dpr = window.devicePixelRatio || 1;
  canvas.width = Math.max(1, Math.round(args.length * dpr));
  canvas.height = Math.round(RULER * dpr);
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

  const colors = themeColors(canvas);
  const horizontal = args.axis === 'x';
  const zoom = args.viewport.zoom;
  const pan = horizontal ? args.viewport.pan.x : args.viewport.pan.y;
  const extent = horizontal ? args.doc.width : args.doc.height;
  const step = niceStep(zoom);

  ctx.fillStyle = colors.bg;
  ctx.fillRect(0, 0, args.length, RULER);

  // Highlight the span the document actually occupies.
  const start = Math.max(0, pan);
  const end = Math.min(args.length, pan + extent * zoom);
  if (end > start) {
    ctx.fillStyle = 'rgba(255,255,255,0.06)';
    if (horizontal) ctx.fillRect(start, 0, end - start, RULER);
    else ctx.fillRect(0, start, RULER, end - start);
  }

  ctx.strokeStyle = colors.line;
  ctx.fillStyle = colors.ink;
  ctx.font = '9px ui-sans-serif, system-ui, sans-serif';
  ctx.textBaseline = 'top';
  ctx.lineWidth = 1;

  const firstTick = Math.floor(-pan / zoom / step) * step;
  for (let value = firstTick; value * zoom + pan <= args.length; value += step) {
    if (value < 0 || value > extent) continue;
    const at = Math.round(value * zoom + pan) + 0.5;
    ctx.beginPath();
    if (horizontal) {
      ctx.moveTo(at, RULER - 6);
      ctx.lineTo(at, RULER);
      ctx.stroke();
      if (value > 0) ctx.fillText(String(value), at + 2, 3);
    } else {
      ctx.moveTo(RULER - 6, at);
      ctx.lineTo(RULER, at);
      ctx.stroke();
      if (value > 0) {
        ctx.save();
        ctx.translate(3, at + 2);
        ctx.rotate(-Math.PI / 2);
        ctx.textAlign = 'right';
        ctx.fillText(String(value), -2, -9);
        ctx.restore();
      }
    }
  }

  // Guide markers on the ruler edge.
  ctx.fillStyle = colors.accent;
  for (const guide of args.guides) {
    if (guide.axis !== args.axis) continue;
    const at = Math.round(guide.position * zoom + pan);
    if (at < 0 || at > args.length) continue;
    if (horizontal) ctx.fillRect(at - 1, 0, 2, RULER);
    else ctx.fillRect(0, at - 1, RULER, 2);
  }

  ctx.strokeStyle = colors.line;
  ctx.beginPath();
  if (horizontal) {
    ctx.moveTo(0, RULER - 0.5);
    ctx.lineTo(args.length, RULER - 0.5);
  } else {
    ctx.moveTo(RULER - 0.5, 0);
    ctx.lineTo(RULER - 0.5, args.length);
  }
  ctx.stroke();
}

export function Rulers({ stageRef }: { stageRef: RefObject<HTMLDivElement | null> }) {
  const showRulers = useEditorStore((s) => s.showRulers);
  const viewport = useEditorStore((s) => s.viewport);
  const docWidth = useEditorStore((s) => s.doc.width);
  const docHeight = useEditorStore((s) => s.doc.height);
  const hostSize = useEditorStore((s) => s.hostSize);
  const guides = useEditorStore((s) => s.guides);
  const startGuide = useGuideDrag(stageRef);

  const topRef = useRef<HTMLCanvasElement | null>(null);
  const leftRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (!showRulers) return;
    const doc = { width: docWidth, height: docHeight };
    if (topRef.current) {
      draw(topRef.current, { axis: 'x', length: hostSize.width, viewport, doc, guides });
    }
    if (leftRef.current) {
      draw(leftRef.current, { axis: 'y', length: hostSize.height, viewport, doc, guides });
    }
  }, [showRulers, viewport, docWidth, docHeight, hostSize, guides]);

  if (!showRulers) return null;

  return (
    <>
      <canvas
        ref={topRef}
        data-testid="ruler-top"
        className="absolute left-0 top-0"
        style={{ width: hostSize.width, height: RULER, cursor: 'ns-resize' }}
        onPointerDown={startGuide('y', null)}
      />
      <canvas
        ref={leftRef}
        data-testid="ruler-left"
        className="absolute left-0 top-0"
        style={{ width: RULER, height: hostSize.height, cursor: 'ew-resize' }}
        onPointerDown={startGuide('x', null)}
      />
      <div
        className="absolute left-0 top-0 border-b border-line bg-surface"
        style={{ width: RULER, height: RULER }}
      />
    </>
  );
}
